<template>
  <div class="finance-dashboard">
    <div class="container">
      <!-- Header Section -->
      <div class="header">
        <h1>Finance Dashboard</h1>
        <p>Overview of cost submissions and financial metrics</p>
      </div>

      <!-- Dashboard Description -->
      <!-- <div class="dashboard-description">
        <div class="description-card">
          <h3>Understanding Your Financial Dashboard</h3>
          <div class="description-content">
            <div class="description-section">
              <h4>Key Metrics Explained:</h4>
              <ul>
                <li><strong>Total Revenue:</strong> The total income generated from all operations this month</li>
                <li><strong>Total Costs:</strong> Direct costs associated with providing services or products</li>
                <li><strong>Total Expenses:</strong> All operational expenses including overhead costs</li>
                <li><strong>Gross Profit:</strong> Revenue minus direct costs (Revenue - Costs)</li>
                <li><strong>EBITDA:</strong> Earnings Before Interest, Taxes, Depreciation, and Amortization</li>
                <li><strong>Active Subsidiaries:</strong> Number of currently operational business units</li>
              </ul>
            </div>
            <div class="description-section">
              <h4>How to Use:</h4>
              <ul>
                <li>Use the subsidiary selector to view data for specific business units</li>
                <li>Navigate between months using the month selector</li>
                <li>Trend indicators show month-over-month changes (↑ for increase, ↓ for decrease)</li>
                <li>Scroll down to view detailed trend graphs for each metric</li>
              </ul>
            </div>
          </div>
        </div>
      </div> -->

      <div class="filter-description">
        <p>
          <i class="fa-solid fa-circle-info"></i>
          Select a subsidiary to view its financial data, or use the month selector to analyze historical performance.
          Trend indicators (↑↓) show month-over-month changes.
        </p>
      </div>

      <br />

      <!-- Filters -->
      <div class="filters">
        <div class="filter-group">
          <div class="subsidiary-selector">
            <div class="selected-subsidiary" @click="toggleSubsidiaryDropdown">
              <span>{{ selectedSubsidiaryName }}</span>
              <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showSubsidiaryDropdown }"></i>
            </div>
            <div class="subsidiary-dropdown" v-if="showSubsidiaryDropdown">
              <div class="search-box">
                <input 
                  type="text" 
                  v-model="searchQuery" 
                  placeholder="Search subsidiaries..."
                  @click.stop
                >
              </div>
              <div 
                v-for="sub in filteredSubsidiaries" 
                :key="sub.id" 
                class="subsidiary-option"
                :class="{ 'active': String(sub.id) === selectedStatsSubsidiary }"
                @click="selectSubsidiary(sub)"
              >
                {{ sub.name }}
              </div>
            </div>
          </div>
          <div class="month-selector">
            <button class="btn-text" @click="previousMonth">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="current-month">{{ formatMonthYear(selectedMonth) }}</span>
            <button class="btn-text" @click="nextMonth">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
          <button class="export-btn" @click="exportToCSV">
            <i class="fa-solid fa-file-export"></i>
            Export to CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-chart-line"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Total Revenue This Month</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Total income from all operations
                  <div class="tooltip-formula">Sum of all income sources</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="totalRevenue" size="large" /></p>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-coins"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Total Costs This Month</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Direct costs of operations
                  <div class="tooltip-formula">Sum of all direct costs</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="totalCosts" size="large" /></p>
            <span class="trend" :class="getTrendClass(costsTrend, true)">
              {{ formatGrowth(costsTrend) }}
            </span>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-receipt"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Total Expenses This Month</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  All operational expenses
                  <div class="tooltip-formula">Sum of all overhead and operational costs</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="totalExpenses" size="large" /></p>
            <span class="trend" :class="getTrendClass(expensesTrend, true)">
              {{ formatGrowth(expensesTrend) }}
            </span>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-hand-holding-dollar"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Gross Profit This Month</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Profit before expenses
                  <div class="tooltip-formula">Gross Profit = Revenue - Costs</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="grossProfit" size="large" /></p>
            <span class="trend" :class="getTrendClass(grossProfitTrend)">
              {{ formatGrowth(grossProfitTrend) }}
            </span>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-chart-simple"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>EBITDA This Month</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Earnings before interest, taxes, depreciation, and amortization
                  <div class="tooltip-formula">EBITDA = Revenue - Costs - Expenses</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="ebitda" size="large" /></p>
            <span class="trend" :class="getTrendClass(ebitdaTrend)">
              {{ formatGrowth(ebitdaTrend) }}
            </span>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-calculator"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Net Profit/Loss Before Tax</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Net profit/(loss) before tax calculation
                  <div class="tooltip-formula">Net Profit/Loss Before Tax = EBITDA - Depreciation of Fixed Assets - Finance Cost</div>
                </div>
              </div>
            </div>
            <p class="amount"><CurrencyDisplay :value="profitBeforeTax" size="large" /></p>
            <span class="trend" :class="getTrendClass(profitBeforeTaxTrend)">
              {{ formatGrowth(profitBeforeTaxTrend) }}
            </span>
          </div>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fa-solid fa-building"></i>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>Active Branches</h3>
              <div class="info-tooltip">
                <i class="fa-solid fa-circle-info"></i>
                <div class="tooltip-content">
                  Number of currently operational business units
                </div>
              </div>
            </div>
            <p class="amount">{{ activeBranches }}</p>
          </div>
        </div>
      </div>

      <!-- Finance Graphs -->
      <div class="graphs-section">
        <div class="graphs-header">
          <div class="graphs-description">
            <p>
              <i class="fa-solid fa-chart-line"></i>
              View detailed trends for each financial metric. Graphs show monthly performance and help identify patterns
              over time.
            </p>
          </div>
          <div class="graphs-controls">
            <div class="year-filter">
              <label>Year:</label>
              <select v-model="selectedGraphYear" @change="updateGraphs" class="year-select">
                <option v-for="year in availableGraphYears" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>
            <button class="export-btn" @click="exportGraphImages">
              <i class="fa-solid fa-image"></i>
              Export Graphs as Images
            </button>
          </div>
        </div>
        <FinanceGraphs ref="financeGraphs" :subsidiaries="subsidiaries" :selectedSubsidiary="selectedStatsSubsidiary" :selectedYear="selectedGraphYear" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import FinanceGraphs from '@/components/FinanceGraphs.vue'
import JSZip from 'jszip'

// State
const totalCosts = ref(0)
const totalExpenses = ref(0)
const costsTrend = ref(0)
const expensesTrend = ref(0)
const activeBranches = ref(0)
const subsidiaries = ref([])
const selectedStatsSubsidiary = ref('')
const selectedStatsMonth = ref('')
const currentYear = new Date().getFullYear()
const selectedGraphYear = ref(currentYear)
const availableGraphYears = ref(Array.from({ length: 5 }, (_, i) => currentYear - 2 + i))
const totalRevenue = ref(0)
const grossProfit = ref(0)
const grossProfitTrend = ref(0)
const ebitda = ref(0)
const ebitdaTrend = ref(0)
const selectedMonth = ref(new Date())
const showSubsidiaryDropdown = ref(false)
const searchQuery = ref('')
const profitBeforeTax = ref(0)
const profitBeforeTaxTrend = ref(0)

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Computed
const monthOptions = computed(() => {
  const months = []
  const currentDate = new Date()
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1)
    months.push({
      label: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
      value: `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`
    })
  }
  return months
})

const selectedSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => String(sub.id) === selectedStatsSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

// Add computed property for filtered subsidiaries
const filteredSubsidiaries = computed(() => {
  if (!searchQuery.value) return subsidiaries.value
  const query = searchQuery.value.toLowerCase()
  return subsidiaries.value.filter(sub => 
    sub.name.toLowerCase().includes(query)
  )
})

// Methods
const fetchDashboardData = async () => {
  try {
    const params = {}
    if (selectedStatsMonth.value) {
      params.month = selectedStatsMonth.value
    }
    if (selectedStatsSubsidiary.value) {
      params.subsidiary_id = selectedStatsSubsidiary.value
    }

    // Get the updated dashboard data (backend now handles adjusted revenue)
    const response = await api.get('/api/finance/dashboard', { params })
    const data = response.data

    // Update all the dashboard values
    totalRevenue.value = data.total_revenue
    totalCosts.value = data.total_costs
    totalExpenses.value = data.total_expenses
    grossProfit.value = data.gross_profit
    ebitda.value = data.ebitda
    costsTrend.value = data.costs_trend
    expensesTrend.value = data.expenses_trend
    grossProfitTrend.value = data.gross_profit_trend
    ebitdaTrend.value = data.ebitda_trend
    activeBranches.value = data.active_branches
    profitBeforeTax.value = data.profit_before_tax
    profitBeforeTaxTrend.value = data.profit_before_tax_trend

  } catch (error) {
    ElMessage.error('Failed to load dashboard data')
  }
}

const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary')
    subsidiaries.value = response.data.sort((a, b) => a.id - b.id)
  } catch (error) {
    ElMessage.error('Failed to load subsidiaries')
  }
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatGrowth = (value) => {
  if (typeof value !== 'number') return '0%'
  return value >= 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`
}

const getTrendClass = (value, isCost = false) => {
  if (typeof value !== 'number') return 'neutral'
  if (isCost) {
    // For costs, higher values are negative
    if (value > 0) return 'negative'
    if (value < 0) return 'positive'
  } else {
    // For other metrics, higher values are positive
    if (value > 0) return 'positive'
    if (value < 0) return 'negative'
  }
  return 'neutral'
}

// Add month formatting function
const formatMonthYear = (date) => {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' })
}

// Add month navigation functions
const previousMonth = () => {
  const newDate = new Date(selectedMonth.value)
  newDate.setMonth(newDate.getMonth() - 1)
  selectedMonth.value = newDate
  selectedStatsMonth.value = `${newDate.getFullYear()}${String(newDate.getMonth() + 1).padStart(2, '0')}`
  // Preserve the current subsidiary selection
  const currentSubsidiary = selectedStatsSubsidiary.value
  fetchDashboardData()
  // Ensure subsidiary selection is maintained
  selectedStatsSubsidiary.value = currentSubsidiary
}

const nextMonth = () => {
  const newDate = new Date(selectedMonth.value)
  newDate.setMonth(newDate.getMonth() + 1)
  selectedMonth.value = newDate
  selectedStatsMonth.value = `${newDate.getFullYear()}${String(newDate.getMonth() + 1).padStart(2, '0')}`
  // Preserve the current subsidiary selection
  const currentSubsidiary = selectedStatsSubsidiary.value
  fetchDashboardData()
  // Ensure subsidiary selection is maintained
  selectedStatsSubsidiary.value = currentSubsidiary
}

const toggleSubsidiaryDropdown = () => {
  showSubsidiaryDropdown.value = !showSubsidiaryDropdown.value
}

const selectSubsidiary = (subsidiary) => {
  selectedStatsSubsidiary.value = String(subsidiary.id)
  showSubsidiaryDropdown.value = false
  fetchDashboardData()
}

// Add click outside handler
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.subsidiary-selector')
  if (dropdown && !dropdown.contains(event.target)) {
    showSubsidiaryDropdown.value = false
  }
}

const exportToCSV = () => {
  // Prepare the data for export
  const data = [
    ['Metric', 'Value', 'Trend'],
    ['Total Revenue', formatCurrency(totalRevenue.value), ''],
    ['Total Costs', formatCurrency(totalCosts.value), formatGrowth(costsTrend.value)],
    ['Total Expenses', formatCurrency(totalExpenses.value), formatGrowth(expensesTrend.value)],
    ['Gross Profit', formatCurrency(grossProfit.value), formatGrowth(grossProfitTrend.value)],
    ['EBITDA', formatCurrency(ebitda.value), formatGrowth(ebitdaTrend.value)],
    ['NET PROFIT/(LOSS) BEFORE TAX', formatCurrency(profitBeforeTax.value), formatGrowth(profitBeforeTaxTrend.value)],
    ['Active Branches', activeBranches.value, '']
  ]

  // Convert to CSV string
  const csvContent = data.map(row => row.join(',')).join('\n')

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `finance_dashboard_${formatMonthYear(selectedMonth.value).replace(' ', '_')}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const updateGraphs = () => {
  // The FinanceGraphs component will watch for selectedYear prop changes
  // No need to do anything here, just trigger reactivity
}

const initializeGraphYears = () => {
  const currentYear = new Date().getFullYear()
  // Include past 2 years, current year, and next 2 years
  availableGraphYears.value = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)
}

const exportGraphImages = async () => {
  try {
    // Get all chart instances from the FinanceGraphs component
    const charts = document.querySelectorAll('canvas')

    // Create a zip file to store all images
    const zip = new JSZip()

    // Convert each chart to an image and add to zip
    for (let i = 0; i < charts.length; i++) {
      const chart = charts[i]
      const chartTitle = chart.closest('.graph-container').querySelector('h2').textContent
      const imageData = chart.toDataURL('image/png')
      const base64Data = imageData.replace(/^data:image\/png;base64,/, '')
      zip.file(`${chartTitle.toLowerCase().replace(' ', '_')}.png`, base64Data, { base64: true })
    }

    // Generate and download the zip file
    const content = await zip.generateAsync({ type: 'blob' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(content)
    link.setAttribute('href', url)
    link.setAttribute('download', `finance_graphs_${formatMonthYear(selectedMonth.value).replace(' ', '_')}.zip`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    ElMessage.error('Failed to export graph images')
  }
}

// Initialize
onMounted(async () => {
  // Set initial month
  selectedMonth.value = new Date()
  selectedStatsMonth.value = `${selectedMonth.value.getFullYear()}${String(selectedMonth.value.getMonth() + 1).padStart(2, '0')}`
  
  // Initialize graph years
  initializeGraphYears()
  
  // Fetch subsidiaries first
  await fetchSubsidiaries()
  
  // Set initial subsidiary if available
  if (subsidiaries.value.length > 0) {
    selectedStatsSubsidiary.value = String(subsidiaries.value[0].id)
  }
  
  // Fetch dashboard data with current selections
  fetchDashboardData()
  
  // Add click outside handler
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.finance-dashboard {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 8px;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
}

.dashboard-description {
  margin-bottom: 32px;
}

.description-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.description-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.description-content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.description-section h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0ea5e9;
  margin-bottom: 12px;
}

.description-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.description-section li {
  font-size: 0.875rem;
  color: #475569;
  margin-bottom: 8px;
  padding-left: 16px;
  position: relative;
}

.description-section li:before {
  content: "•";
  position: absolute;
  left: 0;
  color: #0ea5e9;
}

.description-section strong {
  color: #1e293b;
  font-weight: 500;
}

.filters {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
}

.filter-group {
  display: flex;
  gap: 16px;
  width: 100%;
}

.subsidiary-selector {
  position: relative;
  min-width: 200px;
  flex: 1;
}

.selected-subsidiary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-subsidiary:hover {
  border-color: #cbd5e1;
}

.selected-subsidiary span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  text-align: center;
}

.selected-subsidiary i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-subsidiary i.rotate {
  transform: rotate(180deg);
}

.subsidiary-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
}

.subsidiary-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

.month-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  min-width: 200px;
}

.current-month {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  min-width: 150px;
  text-align: center;
}

.month-selector .btn-text {
  padding: 6px;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.month-selector .btn-text:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.month-selector .btn-text i {
  font-size: 0.875rem;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.export-btn:hover {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 32px;
}

.card {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  height: 100%;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.card-content h3 {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amount {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trend {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.trend.positive {
  background: #dcfce7;
  color: #15803d;
}

.trend.negative {
  background: #fee2e2;
  color: #b91c1c;
}

.trend.neutral {
  background: #f1f5f9;
  color: #475569;
}

.info-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
}

.info-tooltip i {
  font-size: 0.875rem;
  color: #94a3b8;
  transition: color 0.2s;
}

.info-tooltip:hover i {
  color: #0ea5e9;
}

.tooltip-content {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 200px;
}

.tooltip-formula {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-family: monospace;
  color: #94a3b8;
}

.info-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

.filter-description {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.filter-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.filter-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.graphs-section {
  margin-top: 32px;
}

.graphs-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.graphs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.graphs-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.graphs-header .export-btn {
  display: flex;
  max-width: max-content;
  align-items: center;
  gap: 8px;
  padding: 13px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.graphs-header .year-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.graphs-header .year-filter label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
}

.graphs-header .year-select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #1e293b;
  font-size: 0.875rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px;
  padding-right: 32px;
  min-width: 100px;
}

.graphs-header .year-select:hover {
  border-color: #94a3b8;
}

.graphs-header .year-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.graphs-header .export-btn:hover {
  background: #0284c7;
}

.graphs-header .export-btn i {
  font-size: 0.875rem;
}

.graphs-description {
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.graphs-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.graphs-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
}

.search-box input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.1);
}

.search-box input::placeholder {
  color: #94a3b8;
}

@media (max-width: 1536px) {
  .summary-cards {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1024px) {
  .summary-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .finance-dashboard {
    padding: 16px;
  }

  .header {
    margin-bottom: 24px;
  }

  .header h1 {
    font-size: 1.5rem;
  }

  .filters {
    margin-bottom: 24px;
  }

  .filter-group {
    flex-direction: column;
    gap: 12px;
  }

  .subsidiary-selector {
    min-width: 100%;
  }

  .month-selector {
    min-width: 100%;
    justify-content: space-between;
  }

  .current-month {
    min-width: auto;
    flex: 1;
    text-align: center;
  }

  .export-btn {
    width: 100%;
    justify-content: center;
  }

  .summary-cards {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .card {
    padding: 12px;
  }

  .card-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }

  .amount {
    font-size: 1rem;
  }

  .tooltip-content {
    display: none;
  }

  .graphs-header .export-btn {
    max-width: none;
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .finance-dashboard {
    padding: 12px;
  }

  .header h1 {
    font-size: 1.25rem;
  }

  .card {
    padding: 12px;
  }

  .card-content h3 {
    font-size: 0.75rem;
  }

  .amount {
    font-size: 0.875rem;
  }

  .trend {
    font-size: 0.688rem;
  }

  .filter-description p {
    font-size: 0.75rem;
  }

}
</style>